'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useSession, signOut } from 'next-auth/react';
import { Button } from '@/components/ui/button';
import * as lucideIcons from 'lucide-react';
import {
  ShoppingCart,
  Package,
  LayoutDashboard,
  Settings,
  LogOut,
  User,
  Bell,
  Search,
  Menu,
  Phone,
  Clock,
  Trash2,
  Building2,
  Mail,
  X
} from 'lucide-react';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from '@/components/ui/input';
import { DeleteProductDialog } from './cart/DeleteProductDialog';
import { useToast } from './ui/use-toast';
import Image from 'next/image';
import { CartButton } from '@/components/CartButton';
import { useCart } from '@/lib/hooks/useCart';
import { CartDrawer } from '@/components/cart/CartDrawer';
import { CartHoverPreview } from '@/components/cart/CartHoverPreview';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';

interface Product {
  id: string;
  name: string;
  brand: string;
  code: string;
  code2?: string;
  ean?: string;
  price: number;
}

interface HeaderSettings {
  backgroundColor: string;
  textColor: string;
  phoneNumber: string;
  email: string;
  openingHours: string;
}

interface AppearanceSettings {
  id: string;
  headerSettings: HeaderSettings;
  backgroundColor?: string;
  textColor?: string;
}

interface MenuItem {
  id: string;
  name: string;
  href: string;
  icon: string;
  order: number;
  isActive: boolean;
  requiresAuth: boolean;
  adminOnly: boolean;
}

const Navigation = () => {
  const pathname = usePathname();
  const router = useRouter();
  const { data: session } = useSession();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const { cart, updateQuantity, removeItem } = useCart();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [settings, setSettings] = useState<AppearanceSettings | null>(null);
  const [isTopBarVisible, setIsTopBarVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch('/api/appearance/settings');
        if (response.ok) {
          const data = await response.json();
          setSettings(data);
          
          // Aplikuj ustawienia do root elementu
          const root = document.documentElement;
          root.setAttribute('data-theme-primary-color', data.primaryColor);
          root.setAttribute('data-theme-secondary-color', data.secondaryColor);
          root.setAttribute('data-theme-background-color', data.backgroundColor);
          root.setAttribute('data-theme-text-color', data.textColor);
          root.setAttribute('data-button-style', data.buttonStyle);
          
          // Ustaw zmienne CSS
          root.style.setProperty('--theme-primary-color', data.primaryColor);
          root.style.setProperty('--theme-secondary-color', data.secondaryColor);
          root.style.setProperty('--theme-background-color', data.backgroundColor);
          root.style.setProperty('--theme-text-color', data.textColor);
          root.style.setProperty('--button-radius', 
            data.buttonStyle === 'pill' ? '9999px' : 
            data.buttonStyle === 'rounded' ? '0.75rem' : 
            '0.375rem'
          );
        }
      } catch (error) {
        console.error('Błąd podczas pobierania ustawień:', error);
      }
    };

    fetchSettings();
  }, []);

  useEffect(() => {
    if (!isDrawerOpen) {
      // Przywróć scrollowanie po zamknięciu drawera
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }
  }, [isDrawerOpen]);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY === 0) {
        // Pokazuj pasek tylko gdy jesteśmy na samej górze strony
        setIsTopBarVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 50) {
        // Przewijanie w dół i przescrollowano więcej niż 50px
        setIsTopBarVisible(false);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [lastScrollY]);

  useEffect(() => {
    const fetchMenuItems = async () => {
      try {
        const response = await fetch('/api/appearance/menu');
        if (response.ok) {
          const data = await response.json();
          setMenuItems(data);
        }
      } catch (error) {
        console.error('Błąd podczas pobierania menu:', error);
      }
    };

    fetchMenuItems();
  }, [pathname]);

  // Zamknij menu mobilne po zmianie ścieżki
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsMobileSearchOpen(false);
  }, [pathname]);

  useEffect(() => {
    const fetchSearchResults = async () => {
      if (searchQuery.trim().length < 2) {
        setSearchResults([]);
        return;
      }

      try {
        const response = await fetch(`/api/products?search=${encodeURIComponent(searchQuery.trim())}`);
        if (response.ok) {
          const data = await response.json();
          setSearchResults(data.products || []);
        } else {
          setSearchResults([]);
        }
      } catch (error) {
        console.error('Błąd podczas wyszukiwania:', error);
        setSearchResults([]);
      }
    };

    const debounceTimer = setTimeout(fetchSearchResults, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Jeśli jesteśmy już na stronie /products, tylko aktualizujemy parametry URL
      if (pathname === '/products') {
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.set('search', searchQuery.trim());
        const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
        window.history.pushState({}, '', newUrl);
        // Wywołaj zdarzenie popstate, aby zaktualizować widok
        window.dispatchEvent(new PopStateEvent('popstate'));
      } else {
        // W przeciwnym razie przekierowujemy do /products
        router.push(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
      }
      setIsSearchOpen(false);
      setIsMobileSearchOpen(false);
      setSearchQuery('');
    }
  };

  const handleProductSelect = (product: Product) => {
    // Jeśli jesteśmy już na stronie /products, tylko aktualizujemy parametry URL
    if (pathname === '/products') {
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set('search', product.code);
      const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
      window.history.pushState({}, '', newUrl);
      // Wywołaj zdarzenie popstate, aby zaktualizować widok
      window.dispatchEvent(new PopStateEvent('popstate'));
    } else {
      // W przeciwnym razie przekierowujemy do /products
      router.push(`/products?search=${encodeURIComponent(product.code)}`);
    }
    setSearchQuery('');
    setIsSearchOpen(false);
    setIsMobileSearchOpen(false);
  };

  const handleRemoveFromCart = () => {
    toast({
      title: "Produkt usunięty",
      description: "Produkt został usunięty z koszyka",
      variant: "destructive",
    });
  };

  const filteredItems = menuItems
    .filter(item => item.isActive)
    .filter(item => 
      (!item.adminOnly || session?.user?.role === 'ADMIN') &&
      (!item.requiresAuth || session)
    )
    .sort((a, b) => a.order - b.order);

  return (
    <div className="fixed top-0 left-0 right-0 z-40">
      {/* Górny pasek */}
      <div 
        className="top-bar py-2"
        style={{ 
          backgroundColor: settings?.headerSettings?.backgroundColor,
          color: settings?.headerSettings?.textColor,
          transform: `translateY(${isTopBarVisible ? '0' : '-100%'})`,
          transition: 'transform 0.3s ease-in-out',
          zIndex: 41
        }} 
      >
        {settings?.headerSettings && (
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between flex-wrap">
              {/* Informacje kontaktowe - responsywne */}
              <div className="flex items-center space-x-2 md:space-x-6 text-sm flex-wrap">
                <div className="flex items-center">
                  <Phone className="h-4 w-4 mr-1" />
                  <span className="md:block hidden">{settings.headerSettings.phoneNumber}</span>
                  <span className="md:hidden inline-block">
                    <a href={`tel:${settings.headerSettings.phoneNumber.replace(/[ -]/g, '')}`}>
                      {settings.headerSettings.phoneNumber}
                    </a>
                  </span>
                </div>
                <div className="md:flex hidden items-center">
                  <Mail className="h-4 w-4 mr-2" />
                  <span>{settings.headerSettings.email}</span>
                </div>
                <div className="flex items-center">
                  <Clock className="h-4 w-4 mr-1" />
                  <span className="md:block hidden">{settings.headerSettings.openingHours}</span>
                  <span className="md:hidden inline-block">8:00-16:00</span>
                </div>
              </div>
              {session && (
                <div className="text-sm truncate max-w-[200px] md:max-w-none">
                  <span className="md:inline hidden">Zalogowany jako: </span>
                  <span className="font-medium">{session.user.name}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Główna nawigacja */}
      <div 
        className="glass-effect border-b" 
        style={{
          backgroundColor: settings?.backgroundColor || '#ffffff',
          color: settings?.textColor || '#000000',
          transform: `translateY(${isTopBarVisible ? '0' : '-2.5rem'})`,
          transition: 'transform 0.3s ease-in-out',
          zIndex: 40
        }}
      >
        <div className="container mx-auto">
          <div className="h-20 flex items-center justify-between px-4">
            {/* Logo */}
            <Link href="/" className="flex items-center space-x-3">
              <Image
                src="/images/eurotools.svg"
                alt="Euro Tools Logo"
                width={100}
                height={100}
                className="h-16 w-16"
                priority
              />
              <div className="flex flex-col">
                <span className="text-2xl font-bold leading-none md:block hidden">B2B Euro Tools</span>
                <span className="text-lg font-bold leading-none block md:hidden">B2B Euro Tools</span>
                <span className="text-sm text-muted-foreground hidden md:block">Najlepsze ceny na markowe narzędzia</span>
              </div>
            </Link>

            {/* Wyszukiwarka na desktop */}
            <div className="hidden md:flex flex-1 max-w-xl mx-12">
              <div className="relative w-full">
                <Command className="rounded-lg border shadow-md overflow-visible w-full">
                  <div className="flex w-full relative">
                    <form onSubmit={handleSearch} className="flex w-full">
                      <div className="flex-1 relative">
                        <CommandInput
                          placeholder="Wyszukaj po nazwie, kodzie, EAN..."
                          value={searchQuery}
                          onValueChange={setSearchQuery}
                          className="h-11 w-full"
                        />
                        <Button 
                          type="submit" 
                          variant="ghost" 
                          size="sm" 
                          className="absolute right-0 top-0 px-3 h-11"
                        >
                          <Search className="h-4 w-4" />
                        </Button>
                      </div>
                    </form>
                  </div>
                  {searchQuery.length >= 2 && (
                    <CommandList className="absolute w-full bg-white border rounded-lg shadow-lg mt-1 top-[100%] left-0 z-50 max-h-[300px] overflow-y-auto">
                      <CommandEmpty>Brak wyników</CommandEmpty>
                      <CommandGroup heading="Produkty">
                        {searchResults.map((product) => (
                          <CommandItem
                            key={product.id}
                            value={`${product.name} ${product.code} ${product.code2 || ''} ${product.ean || ''}`}
                            onSelect={() => handleProductSelect(product)}
                            className="flex justify-between items-center p-2 cursor-pointer hover:bg-accent"
                          >
                            <div className="flex flex-col gap-1">
                              <div className="font-medium">{product.name}</div>
                              <div className="text-sm text-muted-foreground space-y-1">
                                <div>Marka: {product.brand}</div>
                                <div>Kod: {product.code}{product.code2 ? `, ${product.code2}` : ''}</div>
                                {product.ean && <div>EAN: {product.ean}</div>}
                              </div>
                            </div>
                            <div className="text-sm font-medium">
                              {product.price.toFixed(2)} zł
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  )}
                </Command>
              </div>
            </div>

            {/* Przyciski akcji */}
            <div className="flex items-center gap-3">
              {/* Przycisk wyszukiwania na mobilnych */}
              <Button 
                variant="ghost" 
                size="icon" 
                className="md:hidden"
                onClick={() => setIsMobileSearchOpen(true)}
              >
                <Search className="h-5 w-5" />
              </Button>

              {session ? (
                <>
                  {/* Podgląd koszyka - używamy now HoverCard zamiast Drawer */}
                  <CartHoverPreview />

                  {/* Drawer dla pełnego widoku koszyka po kliknięciu */}
                  <Drawer>
                    <DrawerTrigger asChild>
                      <Button 
                        variant="outline" 
                        className="hidden" 
                        id="cart-drawer-trigger"
                      >
                        Koszyk
                      </Button>
                    </DrawerTrigger>
                    <DrawerContent>
                      <CartDrawer />
                    </DrawerContent>
                  </Drawer>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => signOut({ callbackUrl: '/' })}
                    className="hover:bg-destructive/10 hover:text-destructive"
                  >
                    <LogOut className="h-5 w-5" />
                  </Button>
                </>
              ) : (
                <div className="items-center gap-2 hidden md:flex">
                  <Link href="/auth/register">
                    <Button variant="outline" className="h-11">
                      Zarejestruj się
                    </Button>
                  </Link>
                  <Link href="/auth/login">
                    <Button className="h-11 px-6 shadow-sm">
                      <User className="mr-2 h-5 w-5" />
                      Zaloguj się
                    </Button>
                  </Link>
                </div>
              )}

              {/* Mobilne menu - przycisk hamburger */}
              <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="md:hidden">
                    <Menu className="h-6 w-6" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[80vw] sm:w-[350px] pt-10">
                  <div className="flex flex-col h-full">
                    <div className="flex justify-between items-center mb-8">
                      <h2 className="text-xl font-bold">Menu</h2>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="rounded-full h-8 w-8 p-0"
                      >
                        <X className="h-5 w-5" />
                      </Button>
                    </div>

                    {/* Przyciski logowania w menu mobilnym */}
                    {!session && (
                      <div className="flex flex-col gap-2 mb-6">
                        <Link href="/auth/login" className="w-full" onClick={() => setIsMobileMenuOpen(false)}>
                          <Button className="w-full">
                            Zaloguj się
                          </Button>
                        </Link>
                        <Link href="/auth/register" className="w-full" onClick={() => setIsMobileMenuOpen(false)}>
                          <Button variant="outline" className="w-full">
                            Zarejestruj się
                          </Button>
                        </Link>
                      </div>
                    )}

                    {/* Pozycje menu mobilnego */}
                    <div className="flex flex-col space-y-1">
                      {filteredItems.map((item) => {
                        const IconComponent = item.icon ? (lucideIcons as any)[item.icon] : LayoutDashboard;
                        const isActive = pathname === item.href;
                        return (
                          <Link 
                            key={item.href} 
                            href={item.href}
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            <Button
                              variant={isActive ? 'secondary' : 'ghost'}
                              className={`h-12 w-full justify-start ${
                                isActive ? 'bg-primary/10 text-primary hover:bg-primary/20' : ''
                              }`}
                            >
                              {IconComponent && <IconComponent className="h-5 w-5 mr-3" />}
                              <span className="font-medium">{item.name}</span>
                            </Button>
                          </Link>
                        );
                      })}
                    </div>

                    {/* Informacje kontaktowe w menu mobilnym */}
                    {settings?.headerSettings && (
                      <div className="mt-auto pt-6 border-t text-sm space-y-3">
                        <div className="flex items-center">
                          <Phone className="h-4 w-4 mr-3 text-muted-foreground" />
                          <a href={`tel:${settings.headerSettings.phoneNumber.replace(/[ -]/g, '')}`}>
                            {settings.headerSettings.phoneNumber}
                          </a>
                        </div>
                        <div className="flex items-center">
                          <Mail className="h-4 w-4 mr-3 text-muted-foreground" />
                          <a href={`mailto:${settings.headerSettings.email}`}>
                            {settings.headerSettings.email}
                          </a>
                        </div>
                        <div className="flex items-center">
                          <Clock className="h-4 w-4 mr-3 text-muted-foreground" />
                          <span>{settings.headerSettings.openingHours}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>

          {/* Menu nawigacyjne na desktop */}
          <nav className="h-14 md:flex items-center px-4 border-t hidden">
            <div className="flex space-x-2">
              {filteredItems.map((item) => {
                const IconComponent = item.icon ? (lucideIcons as any)[item.icon] : LayoutDashboard;
                const isActive = pathname === item.href;
                return (
                  <Link key={item.href} href={item.href}>
                    <Button
                      variant={isActive ? 'secondary' : 'ghost'}
                      className={`h-10 ${
                        isActive ? 'bg-primary/10 text-primary hover:bg-primary/20' : ''
                      }`}
                    >
                      {IconComponent && <IconComponent className="h-5 w-5 mr-2" />}
                      <span className="font-medium">{item.name}</span>
                    </Button>
                  </Link>
                );
              })}
            </div>
          </nav>
        </div>
      </div>

      {/* Mobilna wyszukiwarka jako sheet */}
      <Sheet open={isMobileSearchOpen} onOpenChange={setIsMobileSearchOpen}>
        <SheetContent side="top" className="h-[50vh] pt-10">
          <div className="flex flex-col h-full">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Wyszukaj</h2>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => setIsMobileSearchOpen(false)}
                className="rounded-full h-8 w-8 p-0"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
            
            <form onSubmit={handleSearch} className="mb-4">
              <div className="relative">
                <Input
                  type="text"
                  placeholder="Wyszukaj po nazwie, kodzie, EAN..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pr-10 h-12"
                />
                <Button 
                  type="submit" 
                  size="icon" 
                  className="absolute right-0 top-0 h-12 px-3"
                  variant="ghost"
                >
                  <Search className="h-5 w-5" />
                </Button>
              </div>
            </form>
            
            <div className="overflow-y-auto flex-1">
              {searchQuery.length >= 2 && (
                <div className="space-y-4">
                  {searchResults.length === 0 ? (
                    <p className="text-center text-muted-foreground py-4">Brak wyników</p>
                  ) : (
                    searchResults.map((product) => (
                      <div
                        key={product.id}
                        className="border rounded-lg p-3 hover:bg-accent/50 cursor-pointer"
                        onClick={() => handleProductSelect(product)}
                      >
                        <div className="font-medium">{product.name}</div>
                        <div className="text-sm text-muted-foreground mt-1">
                          <div>Marka: {product.brand}</div>
                          <div>Kod: {product.code}</div>
                          <div className="mt-1 font-medium">{product.price.toFixed(2)} zł</div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default Navigation; 